import React, { useRef, useState } from 'react';
import { Box, Typography, Button, Grid, Paper, Divider, Slider, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import GridIcon from '../components/GridIcon';

// Color presets based on color theory
const COLOR_PRESETS = {
  vibrant: {
    name: 'Vibrant Social',
    colors: {
      topLeft: '#FF6B6B',    // Coral Red
      topRight: '#4ECDC4',   // Turquoise
      bottomLeft: '#FFE66D', // Bright Yellow
      bottomRight: '#1A535C' // Deep Teal
    }
  },
  pastel: {
    name: 'Pastel Playful',
    colors: {
      topLeft: '#FFB3BA',    // Soft Pink
      topRight: '#BAFFC9',   // Mint Green
      bottomLeft: '#BAE1FF', // Sky Blue
      bottomRight: '#FFFFBA' // Pale Yellow
    }
  },
  modern: {
    name: 'Modern Tech',
    colors: {
      topLeft: '#6C63FF',    // Electric Purple
      topRight: '#FF6584',   // Hot Pink
      bottomLeft: '#00D9F5', // Cyan
      bottomRight: '#FFB800' // Amber
    }
  },
  nature: {
    name: 'Nature Inspired',
    colors: {
      topLeft: '#2ECC71',    // Emerald
      topRight: '#F1C40F',   // Sunflower
      bottomLeft: '#3498DB', // Ocean Blue
      bottomRight: '#E74C3C' // Ruby
    }
  },
  complementary: {
    name: 'Complementary',
    colors: {
      topLeft: '#FF6B6B',    // Coral Red
      topRight: '#4ECDC4',   // Turquoise
      bottomLeft: '#FF6B6B', // Coral Red
      bottomRight: '#4ECDC4' // Turquoise
    }
  },
  triadic: {
    name: 'Triadic',
    colors: {
      topLeft: '#FF6B6B',    // Red
      topRight: '#6BFF6B',   // Green
      bottomLeft: '#6B6BFF', // Blue
      bottomRight: '#FF6B6B' // Red
    }
  },
  tetradic: {
    name: 'Tetradic',
    colors: {
      topLeft: '#FF6B6B',    // Red
      topRight: '#6BFF6B',   // Green
      bottomLeft: '#6B6BFF', // Blue
      bottomRight: '#FFFF6B' // Yellow
    }
  },
  analogous: {
    name: 'Analogous',
    colors: {
      topLeft: '#FF6B6B',    // Red
      topRight: '#FF8F6B',   // Orange-Red
      bottomLeft: '#FFB36B', // Orange
      bottomRight: '#FFD76B' // Yellow-Orange
    }
  },
  monochromatic: {
    name: 'Monochromatic',
    colors: {
      topLeft: '#FF6B6B',    // Base Red
      topRight: '#FF8F8F',   // Lighter Red
      bottomLeft: '#FF4747', // Darker Red
      bottomRight: '#FFB3B3' // Lightest Red
    }
  },
  sunset: {
    name: 'Sunset Vibes',
    colors: {
      topLeft: '#FF6B6B',    // Coral
      topRight: '#FFB36B',   // Peach
      bottomLeft: '#FFD76B', // Yellow
      bottomRight: '#FF8F6B' // Salmon
    }
  },
  ocean: {
    name: 'Ocean Waves',
    colors: {
      topLeft: '#4ECDC4',    // Turquoise
      topRight: '#45B7AF',   // Teal
      bottomLeft: '#3BA1A9', // Deep Teal
      bottomRight: '#318BA3' // Ocean Blue
    }
  },
  forest: {
    name: 'Forest Fresh',
    colors: {
      topLeft: '#2ECC71',    // Emerald
      topRight: '#27AE60',   // Forest Green
      bottomLeft: '#1E8449', // Dark Green
      bottomRight: '#16A085' // Sea Green
    }
  },
  berry: {
    name: 'Berry Blend',
    colors: {
      topLeft: '#E74C3C',    // Ruby
      topRight: '#C0392B',   // Dark Red
      bottomLeft: '#8E44AD', // Purple
      bottomRight: '#9B59B6' // Lavender
    }
  },
  neon: {
    name: 'Neon Nights',
    colors: {
      topLeft: '#FF00FF',    // Magenta
      topRight: '#00FFFF',   // Cyan
      bottomLeft: '#FFFF00', // Yellow
      bottomRight: '#00FF00' // Green
    }
  },
  retro: {
    name: 'Retro Wave',
    colors: {
      topLeft: '#FF6B6B',    // Coral
      topRight: '#6B6BFF',   // Indigo
      bottomLeft: '#FFB36B', // Orange
      bottomRight: '#6BFF6B' // Lime
    }
  }
};

// App icon sizes for iOS, Android, and web with their purposes
const ICON_SIZES = [
  { size: 1024, label: "App Store / Play Store / macOS App" },
  { size: 512, label: "Play Store Listing" },
  { size: 192, label: "Android Home Screen / Web App Manifest" },
  { size: 180, label: "iPhone Home Screen (iPhone 6 Plus, X, XS, XR, 11, 12, 13, 14, 15)" },
  { size: 167, label: "iPad Pro Home Screen" },
  { size: 152, label: "iPad / iPad Mini Home Screen" },
  { size: 120, label: "iPhone Spotlight Search / iPad Notification Center" },
  { size: 87, label: "iPhone Settings / iPad Spotlight Search" },
  { size: 86, label: "Apple Watch Home Screen" },
  { size: 48, label: "Apple Watch Notification Center" },
  { size: 40, label: "iPhone Notification Center / Apple Watch Companion Settings" },
  { size: 32, label: "macOS Finder / Windows Taskbar" },
  { size: 16, label: "macOS Finder (small) / Browser Tab" },
];

const SPLASH_WIDTH = 1242;
const SPLASH_HEIGHT = 2436;
const SPLASH_ICON_SIZE = 1242; // The icon itself is a square centered in the splash

function downloadSplashAsPNG(svgElement: SVGSVGElement) {
  const width = SPLASH_WIDTH;
  const height = SPLASH_HEIGHT;
  const iconSize = SPLASH_ICON_SIZE;
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElement);
  const img = new window.Image();
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  img.onload = function () {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
      // Center the icon vertically
      const x = 0;
      const y = (height - iconSize) / 2;
      ctx.drawImage(img, x, y, iconSize, iconSize);
      canvas.toBlob((blob) => {
        if (blob) {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `GridIcon_${width}x${height}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        URL.revokeObjectURL(url);
      }, 'image/png');
    }
  };
  img.src = url;
}

// Function to convert SVG to ICO format
async function downloadAsICO(svgElement: SVGSVGElement) {
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElement);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  
  // Create images for all required sizes
  const sizes = [64, 32, 24, 16];
  const images = sizes.map(size => new window.Image());
  const canvases = sizes.map(size => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    return canvas;
  });
  
  const contexts = canvases.map(canvas => canvas.getContext('2d'));
  if (contexts.some(ctx => !ctx)) return;
  
  // Load all images
  await Promise.all(images.map((img, idx) => 
    new Promise((resolve) => {
      img.onload = resolve;
      img.src = url;
    })
  ));
  
  // Draw images to canvases
  contexts.forEach((ctx, idx) => {
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, sizes[idx], sizes[idx]);
    ctx.drawImage(images[idx], 0, 0, sizes[idx], sizes[idx]);
  });
  
  // Convert to PNG blobs
  const blobs = await Promise.all(canvases.map(canvas => 
    new Promise<Blob>((resolve) => canvas.toBlob((blob) => resolve(blob!), 'image/png'))
  ));
  
  // Create ICO file structure
  const icoHeader = new Uint8Array(6 + (16 * blobs.length));
  const icoHeaderView = new DataView(icoHeader.buffer);
  
  // Write ICO header
  icoHeaderView.setUint16(0, 0, true); // Reserved
  icoHeaderView.setUint16(2, 1, true); // Type (1 for ICO)
  icoHeaderView.setUint16(4, blobs.length, true); // Number of images
  
  // Calculate total size and create final buffer
  let offset = 6 + (16 * blobs.length);
  const imageData = await Promise.all(blobs.map(blob => blob.arrayBuffer()));
  const totalSize = offset + imageData.reduce((sum, data) => sum + data.byteLength, 0);
  const icoFile = new Uint8Array(totalSize);
  
  // Copy header
  icoFile.set(icoHeader);
  
  // Write image entries and data
  imageData.forEach((data, idx) => {
    const entryOffset = 6 + (16 * idx);
    const dataOffset = offset;
    
    // Write image entry
    const width = sizes[idx] === 256 ? 0 : sizes[idx];
    const height = sizes[idx] === 256 ? 0 : sizes[idx];
    icoHeaderView.setUint8(entryOffset, width);
    icoHeaderView.setUint8(entryOffset + 1, height);
    icoHeaderView.setUint8(entryOffset + 2, 0); // No color palette
    icoHeaderView.setUint8(entryOffset + 3, 0); // Reserved
    icoHeaderView.setUint16(entryOffset + 4, 1, true); // Color planes
    icoHeaderView.setUint16(entryOffset + 6, 32, true); // Bits per pixel
    icoHeaderView.setUint32(entryOffset + 8, data.byteLength, true); // Size of image data
    icoHeaderView.setUint32(entryOffset + 12, dataOffset, true); // Offset to image data
    
    // Copy image data
    icoFile.set(new Uint8Array(data), dataOffset);
    offset += data.byteLength;
  });
  
  // Create download link
  const blob = new Blob([icoFile], { type: 'image/x-icon' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'favicon.ico';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  
  URL.revokeObjectURL(url);
}

const GridIconExport: React.FC = () => {
  // Refs for each SVG
  const svgRefs = useRef<(SVGSVGElement | null)[]>([]);
  const splashRef = useRef<SVGSVGElement | null>(null);
  const faviconRef = useRef<SVGSVGElement | null>(null);
  const previewRef = useRef<SVGSVGElement | null>(null);

  // State for icon variations
  const [iconVariation, setIconVariation] = useState({
    padding: 25, // Increased from 10 to make graphic 30% smaller
    strokeWidth: 10,
    borderRadius: 8,
    color: '#000000',
    backgroundColor: '#ffffff',
    rotation: 0,
    quadrantColors: COLOR_PRESETS.vibrant.colors,
    centerSquare: {
      color: '#000000',
      strokeWidth: 10,
      borderRadius: 8,
      rotation: 0
    }
  });

  const handlePresetChange = (preset: keyof typeof COLOR_PRESETS) => {
    setIconVariation(prev => ({
      ...prev,
      quadrantColors: COLOR_PRESETS[preset].colors
    }));
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        GridIcon Exporter
      </Typography>
      <Typography variant="body1" gutterBottom>
        Download the GridIcon in all required app icon sizes as PNG images.
      </Typography>

      {/* Icon Variation Preview Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom>
          Icon Variations
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Preview
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: iconVariation.backgroundColor,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <span
                    ref={el => {
                      if (el) {
                        const svg = el.querySelector('svg');
                        previewRef.current = svg;
                      }
                    }}
                    style={{
                      transform: `rotate(${iconVariation.rotation}deg)`,
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    <GridIcon 
                      size={160}
                      color={iconVariation.color}
                      filledQuadrants={[1, 2, 3, 4]}
                      fillColors={[
                        iconVariation.quadrantColors.topRight,
                        iconVariation.quadrantColors.topLeft,
                        iconVariation.quadrantColors.bottomLeft,
                        iconVariation.quadrantColors.bottomRight
                      ]}
                    />
                  </span>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Properties
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Color Presets */}
                <Box>
                  <Typography gutterBottom>Color Presets</Typography>
                  <ToggleButtonGroup
                    value={Object.keys(COLOR_PRESETS).find(key => 
                      JSON.stringify(COLOR_PRESETS[key as keyof typeof COLOR_PRESETS].colors) === 
                      JSON.stringify(iconVariation.quadrantColors)
                    )}
                    exclusive
                    onChange={(_, value) => value && handlePresetChange(value as keyof typeof COLOR_PRESETS)}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                      <ToggleButton key={key} value={key}>
                        {preset.name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {/* Quadrant Colors */}
                <Box>
                  <Typography gutterBottom>Quadrant Colors</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Top Left"
                        type="color"
                        value={iconVariation.quadrantColors.topLeft}
                        onChange={(e) => setIconVariation(prev => ({
                          ...prev,
                          quadrantColors: {
                            ...prev.quadrantColors,
                            topLeft: e.target.value
                          }
                        }))}
                        InputProps={{
                          style: { height: '56px' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Top Right"
                        type="color"
                        value={iconVariation.quadrantColors.topRight}
                        onChange={(e) => setIconVariation(prev => ({
                          ...prev,
                          quadrantColors: {
                            ...prev.quadrantColors,
                            topRight: e.target.value
                          }
                        }))}
                        InputProps={{
                          style: { height: '56px' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Bottom Left"
                        type="color"
                        value={iconVariation.quadrantColors.bottomLeft}
                        onChange={(e) => setIconVariation(prev => ({
                          ...prev,
                          quadrantColors: {
                            ...prev.quadrantColors,
                            bottomLeft: e.target.value
                          }
                        }))}
                        InputProps={{
                          style: { height: '56px' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Bottom Right"
                        type="color"
                        value={iconVariation.quadrantColors.bottomRight}
                        onChange={(e) => setIconVariation(prev => ({
                          ...prev,
                          quadrantColors: {
                            ...prev.quadrantColors,
                            bottomRight: e.target.value
                          }
                        }))}
                        InputProps={{
                          style: { height: '56px' }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Center Square Controls */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Center Square
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Color"
                      type="color"
                      value={iconVariation.centerSquare.color}
                      onChange={(e) => setIconVariation(prev => ({
                        ...prev,
                        centerSquare: {
                          ...prev.centerSquare,
                          color: e.target.value
                        }
                      }))}
                      InputProps={{
                        style: { height: '56px' }
                      }}
                    />
                    <Box>
                      <Typography gutterBottom>Stroke Width (%)</Typography>
                      <Slider
                        value={iconVariation.centerSquare.strokeWidth}
                        onChange={(_, value) => setIconVariation(prev => ({
                          ...prev,
                          centerSquare: {
                            ...prev.centerSquare,
                            strokeWidth: value as number
                          }
                        }))}
                        min={1}
                        max={20}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom>Border Radius (%)</Typography>
                      <Slider
                        value={iconVariation.centerSquare.borderRadius}
                        onChange={(_, value) => setIconVariation(prev => ({
                          ...prev,
                          centerSquare: {
                            ...prev.centerSquare,
                            borderRadius: value as number
                          }
                        }))}
                        min={0}
                        max={50}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom>Rotation (degrees)</Typography>
                      <Slider
                        value={iconVariation.centerSquare.rotation}
                        onChange={(_, value) => setIconVariation(prev => ({
                          ...prev,
                          centerSquare: {
                            ...prev.centerSquare,
                            rotation: value as number
                          }
                        }))}
                        min={0}
                        max={360}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography gutterBottom>Padding (%)</Typography>
                  <Slider
                    value={iconVariation.padding}
                    onChange={(_, value) => setIconVariation(prev => ({ ...prev, padding: value as number }))}
                    min={0}
                    max={30}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box>
                  <Typography gutterBottom>Stroke Width (%)</Typography>
                  <Slider
                    value={iconVariation.strokeWidth}
                    onChange={(_, value) => setIconVariation(prev => ({ ...prev, strokeWidth: value as number }))}
                    min={1}
                    max={20}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box>
                  <Typography gutterBottom>Border Radius (%)</Typography>
                  <Slider
                    value={iconVariation.borderRadius}
                    onChange={(_, value) => setIconVariation(prev => ({ ...prev, borderRadius: value as number }))}
                    min={0}
                    max={20}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box>
                  <Typography gutterBottom>Rotation (degrees)</Typography>
                  <Slider
                    value={iconVariation.rotation}
                    onChange={(_, value) => setIconVariation(prev => ({ ...prev, rotation: value as number }))}
                    min={0}
                    max={360}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Icon Color"
                    type="color"
                    value={iconVariation.color}
                    onChange={(e) => setIconVariation(prev => ({ ...prev, color: e.target.value }))}
                    InputProps={{
                      style: { height: '56px' }
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Background Color"
                    type="color"
                    value={iconVariation.backgroundColor}
                    onChange={(e) => setIconVariation(prev => ({ ...prev, backgroundColor: e.target.value }))}
                    InputProps={{
                      style: { height: '56px' }
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Add Favicon.ico download section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Favicon.ico
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span
            ref={el => {
              if (el) {
                const svg = el.querySelector('svg');
                faviconRef.current = svg;
              }
            }}
            style={{
              display: 'inline-block',
              background: '#fff',
              borderRadius: 4,
              width: 32,
              height: 32,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              padding: 2,
            }}
          >
            <GridIcon size={32} color="#000000" filledQuadrants={[1, 2, 3, 4]} fillColors={['#000000', '#000000', '#000000', '#000000']} />
          </span>
          <Button
            variant="contained"
            onClick={() => {
              if (faviconRef.current) {
                downloadAsICO(faviconRef.current);
              }
            }}
          >
            Download Favicon.ico
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {ICON_SIZES.map((iconInfo, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={iconInfo.size}>
            <Paper elevation={3} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle1" gutterBottom>
                {iconInfo.size} x {iconInfo.size}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {iconInfo.label}
              </Typography>
              <Box
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  mb: 2,
                  height: 120,
                  width: '100%'
                }}
              >
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    if (el) {
                      const svg = el.querySelector('svg');
                      svgRefs.current[idx] = svg;
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: iconInfo.size === 192 ? 'transparent' : '#fff',
                    borderRadius: 1,
                    width: 96,
                    height: 96,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    '& svg': {
                      maxWidth: '80px',
                      maxHeight: '80px',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}
                >
                  <GridIcon size={Math.min(iconInfo.size, 80)} color="#000000" filledQuadrants={[1, 2, 3, 4]} fillColors={['#000000', '#000000', '#000000', '#000000']} />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={() => {
                  const svg = svgRefs.current[idx];
                  if (svg) {
                    // For square icons, width = height = size
                    const sizeVal = iconInfo.size;
                    const serializer = new XMLSerializer();
                    const svgString = serializer.serializeToString(svg);
                    const img = new window.Image();
                    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                    const url = URL.createObjectURL(svgBlob);
                    img.onload = function () {
                      const canvas = document.createElement('canvas');
                      canvas.width = sizeVal;
                      canvas.height = sizeVal;
                      const ctx = canvas.getContext('2d');
                      if (ctx) {
                        // Only fill white background for non-192 sizes
                        if (sizeVal !== 192) {
                          ctx.fillStyle = '#fff';
                          ctx.fillRect(0, 0, sizeVal, sizeVal);
                        }
                        ctx.drawImage(img, 0, 0, sizeVal, sizeVal);
                        canvas.toBlob((blob) => {
                          if (blob) {
                            const a = document.createElement('a');
                            a.href = URL.createObjectURL(blob);
                            a.download = `GridIcon_${sizeVal}x${sizeVal}.png`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }
                          URL.revokeObjectURL(url);
                        }, 'image/png');
                      }
                    };
                    img.src = url;
                  }
                }}
              >
                Download PNG
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ my: 6 }} />
      <Typography variant="h5" gutterBottom>
        Splash Icon
      </Typography>
      <Typography variant="body2" gutterBottom>
        Background: 1242 x 2436 (portrait), icon centered and sized 1242 x 1242
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 2 }}>
        <Box
          sx={{
            display: 'inline-flex',
            background: '#fff',
            borderRadius: 4,
            width: 64,
            height: 128,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            p: 0,
          }}
        >
          {/* Center the icon in a tall background */}
          <span
            ref={el => {
              if (el) {
                const svg = el.querySelector('svg');
                splashRef.current = svg;
              }
            }}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '100%',
              height: 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <GridIcon size={SPLASH_ICON_SIZE} color="#000000" filledQuadrants={[1, 2, 3, 4]} fillColors={['#000000', '#000000', '#000000', '#000000']} />
          </span>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            if (splashRef.current) {
              downloadSplashAsPNG(splashRef.current);
            }
          }}
        >
          Download Splash PNG
        </Button>
      </Box>
    </Box>
  );
};

export default GridIconExport; 